/**
 * Simplified utility for resolving values from data using path notation
 * Supports plain strings, dot notation, array notation, and special values
 */
const resolve = (path, data) => {
    // Handle null/undefined inputs
    if (!path || data === null || data === undefined) {
        return null;
    }
    
    // Convert path to string if it's not already
    const pathStr = String(path);
    
    // Handle special values
    if (pathStr === '${now}') {
        return new Date().toISOString();
    }

    if (pathStr === '${today}') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today.toISOString();
    }
    
    // Remove ${} wrapper if present
    let cleanPath = pathStr;
    if (pathStr.startsWith('${') && pathStr.endsWith('}')) {
        cleanPath = pathStr.slice(2, -1);
    }
    
    // Handle array notation with optional index access like 'companies[].name[0]'
    if (cleanPath.includes('[]')) {
        return resolveArrayPath(cleanPath, data);
    }
    
    // Handle dot notation
    return resolveDotPath(cleanPath, data);
};

/**
 * Resolve dot notation paths like 'setting.price' or 'output.data.quantity'
 * Also supports slash notation paths like 'setting/price' for backward compatibility
 */
const resolveDotPath = (path, data) => {
    // Support both dot notation and slash notation
    const separator = path.includes('/') ? '/' : '.';
    const parts = path.split(separator);
    let result = data;
    
    for (const part of parts) {
        if (result === null || result === undefined) {
            return null;
        }

        // Support simple array index notation within a segment, e.g. 'paymentTransaction[0]'
        if (part.includes('[')) {
            let current = result;
            let remaining = part;

            // Extract property name before the first '[' (if any)
            const propertyMatch = remaining.match(/^([^\[\]]*)/);
            const propertyName = propertyMatch ? propertyMatch[1] : '';

            if (propertyName) {
                current = current[propertyName];
            }

            if (current === null || current === undefined) {
                return null;
            }

            // Process one or more indices like [0][1]
            const indicesPart = remaining.slice(propertyName.length);
            const indexRegex = /\[(\d+)\]/g;
            let match;
            while ((match = indexRegex.exec(indicesPart)) !== null) {
                const index = parseInt(match[1], 10);
                if (!Array.isArray(current) || index < 0 || index >= current.length) {
                    return null;
                }
                current = current[index];
            }

            result = current;
        } else {
            result = result[part];
        }
    }
    
    return result === undefined ? null : result;
};

/**
 * Resolve array notation paths like 'lines[].itemId' or 'data.lines[].origin.locations[].itemId'
 * Also supports indexed access like 'companies[].name[0]'
 */
const resolveArrayPath = (path, data) => {
    // Check for indexed access pattern like 'companies[].name[0]'
    const indexedMatch = path.match(/^(.+\[\])(.+)\[(\d+)\]$/);
    if (indexedMatch) {
        const [, arrayPath, fieldPath, indexStr] = indexedMatch;
        const index = parseInt(indexStr, 10);
        
        // First resolve the array path to get all field values
        const arrayResult = resolveArrayPath(arrayPath + fieldPath, data);
        
        if (Array.isArray(arrayResult) && index >= 0 && index < arrayResult.length) {
            return arrayResult[index];
        }
        return null;
    }
    
    // Split path into segments by []
    const segments = path.split('[]');
    
    if (segments.length < 2) {
        console.warn(`Invalid array path format: ${path}. Expected format with [] notation.`);
        return null;
    }
    
    let currentData = data;
    
    // Process each segment
    for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        
        if (i === 0) {
            // First segment: navigate to the array
            if (segment) {
                currentData = resolveDotPath(segment, currentData);
            }
            // Check if the resolved path exists - fail gracefully if not
            if (currentData === undefined || currentData === null) {
                return null;
            }
        } else if (i === segments.length - 1) {
            // Last segment: extract field from each array element
            if (!Array.isArray(currentData)) {
                console.warn(`Expected array at path segment, got: ${typeof currentData}`);
                return null;
            }
            
            if (!segment || segment.startsWith('.')) {
                // No field specified or starts with dot, return entire array or process remaining path
                const fieldPath = segment.startsWith('.') ? segment.substring(1) : segment;
                
                if (!fieldPath) {
                    // Return entire array
                    return currentData;
                }
                
                // Extract field from each element
                return currentData.map(item => {
                    if (item === null || item === undefined) return null;
                    return resolveDotPath(fieldPath, item);
                });
            } else {
                // Field specified without leading dot
                return currentData.map(item => {
                    if (item === null || item === undefined) return null;
                    return resolveDotPath(segment, item);
                });
            }
        } else {
            // Middle segment: process nested arrays
            if (currentData === undefined || currentData === null) {
                return null;
            }
            if (!Array.isArray(currentData)) {
                console.warn(`Expected array at path segment, got: ${typeof currentData}`);
                return null;
            }
            
            // Navigate to nested arrays and flatten
            const nestedArrays = [];
            for (const item of currentData) {
                if (item === null || item === undefined) continue;
                
                let nestedData = item;
                if (segment && segment.startsWith('.')) {
                    const fieldPath = segment.substring(1);
                    nestedData = resolveDotPath(fieldPath, item);
                } else if (segment) {
                    nestedData = resolveDotPath(segment, item);
                }
                
                if (Array.isArray(nestedData)) {
                    nestedArrays.push(...nestedData);
                } else if (nestedData !== undefined) {
                    nestedArrays.push(nestedData);
                }
            }
            
            currentData = nestedArrays;
        }
    }
    
    return currentData;
};

export default resolve;
